import { Suspense, useMemo, useState } from 'react';

import { Navigate } from 'react-router';

import { BreadCrumbs } from './components/BreadCrumbs';
import { FilterBar } from './components/Filters';
import { ReferBanner } from './components/ReferBanner';
import { Sellers } from './components/Sellers';
import {
  useGetBuyerSellerDirectory,
  useGetApplication,
} from '../../../api/lending/lapiHooks';
import { CustomErrorBoundary } from '../../../AppWrapper';
import { useAccountNavigation } from '../../../hooks/useAccountNavigation';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import {
  SpendingLimitCard,
  SpendingLimitCardSkeleton,
} from '../components/SpendingLimitCard/SpendingLimitCard';
import { SpendingLimitDrawer } from '../components/SpendingLimitDrawer/SpendingLimitDrawer';
import { useRefreshSpendingLimits } from '../hooks/useRefreshSpendingLimits';

export function SellerDirectory() {
  const { getAccountUrl } = useAccountNavigation();
  const { sellers: rawSellers, loadingSellerDirectory } =
    useGetBuyerSellerDirectory();
  const { application: rawApplication, fetchApplication } = useGetApplication();
  const isMobile = useMediaQuery('(max-width: 624px)');

  const sellers = rawSellers ?? null;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedPricings, setSelectedPricings] = useState<string[]>([]);
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);

  const [isSpendingLimitDrawerOpen, setIsSpendingLimitDrawerOpen] =
    useState(false);

  const {
    refreshSpendingLimit,
    isLoading: loadingRefreshSpendingLimit,
    errorMessage: refreshSpendingLimitErrorMessage,
  } = useRefreshSpendingLimits(() => {
    fetchApplication();
  });

  const handleRefreshSpendingLimit = async () => {
    try {
      await refreshSpendingLimit();
    } catch {
      // do nothing
    }
  };

  const displaySellers = useMemo(
    () =>
      (sellers ?? [])
        .filter((seller) => {
          const matchesSearch =
            !searchQuery ||
            seller.name.toLowerCase().includes(searchQuery.toLowerCase());

          const matchesCategory =
            selectedCategories.length === 0 ||
            selectedCategories.includes(seller.industry);

          const matchesTermsAndPricing =
            selectedTerms.length === 0 && selectedPricings.length === 0
              ? true
              : (Array.isArray(seller.offerings) ? seller.offerings : []).some(
                  (offering) => {
                    const matchesTerm =
                      selectedTerms.length === 0 ||
                      selectedTerms.includes(offering.product_type);
                    const matchesPricing =
                      selectedPricings.length === 0 ||
                      selectedPricings.includes(offering.fee_structure);
                    return matchesTerm && matchesPricing;
                  },
                );

          return matchesSearch && matchesCategory && matchesTermsAndPricing;
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sellers, searchQuery, selectedCategories, selectedPricings, selectedTerms],
  );

  if (!loadingSellerDirectory && !sellers) {
    return <Navigate to={getAccountUrl(window.location.search)} />;
  }

  const spendingLimitSection = (
    <div className="mb-m">
      <CustomErrorBoundary errorComponent={<></>}>
        <Suspense fallback={<SpendingLimitCardSkeleton />}>
          <SpendingLimitCard
            setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
            refreshSpendingLimit={handleRefreshSpendingLimit}
            loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
          />
        </Suspense>
      </CustomErrorBoundary>
      <SpendingLimitDrawer
        isOpen={isSpendingLimitDrawerOpen}
        setIsOpen={setIsSpendingLimitDrawerOpen}
        refreshSpendingLimit={handleRefreshSpendingLimit}
        loadingRefreshSpendingLimit={loadingRefreshSpendingLimit}
        errorMessage={refreshSpendingLimitErrorMessage}
        application={rawApplication}
      />
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-72px)] bg-gradient-to-r from-structure-100 to-secondary-97 pt-[112px]">
      <div className="px-5 pb-5 mx-auto max-w-[1136px]">
        <div className="mb-2xl flex flex-row items-center gap-m">
          <BreadCrumbs />
        </div>
        {spendingLimitSection}
        <div
          className="grid gap-x-[40px]"
          style={{
            gridTemplateAreas: isMobile
              ? "'heading' 'filters' 'sellers'"
              : "'heading heading' 'filters sellers'",
            gridTemplateColumns: isMobile ? '1fr' : '300px 1fr',
            gridTemplateRows: isMobile ? 'auto auto 1fr' : 'auto 1fr',
          }}
        >
          <div style={{ gridArea: 'heading' }}>
            <h2 className="mb-xs text-2xl font-bold">
              Featured iwocaPay Suppliers
            </h2>
            <span className="mb-xl block text-s font-reg">
              Select a supplier to open their website.
            </span>
          </div>
          <div
            className="rounded-m border border-solid border-secondary-90 bg-secondary-97 px-l py-l"
            style={{ gridArea: 'filters' }}
          >
            <FilterBar
              sellers={sellers}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              selectedPricings={selectedPricings}
              setSelectedPricings={setSelectedPricings}
              selectedTerms={selectedTerms}
              setSelectedTerms={setSelectedTerms}
            />
            {!isMobile && (
              <div className="mt-xl">
                <ReferBanner />
              </div>
            )}
          </div>
          <div style={{ gridArea: 'sellers' }}>
            {isMobile && (
              <div className="mb-l">
                <ReferBanner />
              </div>
            )}
            <Sellers sellers={sellers} displaySellers={displaySellers} />
          </div>
        </div>
      </div>
    </div>
  );
}
